import {
  Box, Button, Checkbox, Container, FormControl, FormControlLabel, Grid,
  IconButton, InputLabel, Modal, OutlinedInput, Radio, RadioGroup, Switch,
  TextField,
} from '@material-ui/core';
import { EditOutlined, DeleteForeverOutlined, Close, Search } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Dashboard from '../../../Components/Dashboard';
import Denied from '../../../Components/Denied';
import history from '../../../Services/history';
import { store } from '../../../store';
import { apiLabs } from '../../../Services/api';
import stylePagesEmbarque from './style';
import HolidayModal from './HolidayModal';

// Dias da semana (chave no backend + rótulo curto)
const WEEK_DAYS = [
  { key: 'works_monday', label: 'Seg' },
  { key: 'works_tuesday', label: 'Ter' },
  { key: 'works_wednesday', label: 'Qua' },
  { key: 'works_thursday', label: 'Qui' },
  { key: 'works_friday', label: 'Sex' },
  { key: 'works_saturday', label: 'Sáb' },
  { key: 'works_sunday', label: 'Dom' },
];

// Estado inicial de uma nova transportadora
// Porte do pedido que a transportadora normalmente leva. O backend persiste
// em minusculo e sem acento; aqui guardamos tambem o rotulo exibido.
const ORDER_SIZE_OPTIONS = [
  { value: "pequeno", label: "Pequeno" },
  { value: "medio", label: "Médio" },
  { value: "grande", label: "Grande" },
];
const orderSizeLabel = (value) =>
  ORDER_SIZE_OPTIONS.find((option) => option.value === value)?.label || "—";

// Como a transportadora expressa o tempo de expedição. É só de interface —
// NÃO vai no payload. O backend recebe time_expedition OU
// time_expedition_hours, nunca os dois (responde 400 se vierem juntos).
const EXPEDITION_MODES = {
  days: 'days',
  hours: 'hours',
};

const EMPTY_CARRIER = {
  supplier_name: '',
  name: '',
  expedition_mode: EXPEDITION_MODES.days,
  time_expedition: 1,
  time_expedition_hours: '',
  time_cut: '16:00',
  time_collection: '18:00',
  cut_tolerance_minutes: 15,
  is_active: true,
  is_marketplace: false,
  order_size: "",
  works_monday: true,
  works_tuesday: true,
  works_wednesday: true,
  works_thursday: true,
  works_friday: true,
  works_saturday: false,
  works_sunday: false,
};

// Converte time do backend (TIME 'HH:MM:SS', inteiro legado, ou 'HH:MM') → 'HH:MM' pro input
function toTimeInput(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'number') return `${String(v).padStart(2, '0')}:00`;
  const s = String(v);
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m) return `${m[1].padStart(2, '0')}:${m[2]}`;
  if (/^\d{1,2}$/.test(s)) return `${s.padStart(2, '0')}:00`;
  return s;
}

// A transportadora é expressa (conta o prazo em horas) quando o backend
// devolve time_expedition_hours preenchido.
// Dias de expedição: '' => null (não informado), '0' => 0 (válido).
function parseDiasExpedicao(value) {
  const raw = String(value ?? '').trim();
  if (raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function isExpressCarrier(c) {
  return !!c?.time_expedition_hours;
}

// 'HH:MM:SS' | 'HH:MM' → '1h30' / '3h'. Zeros à esquerda saem: na listagem
// "01h30" compete visualmente com um horário de corte, e não é horário.
function formatExpeditionHours(value) {
  const time = toTimeInput(value);
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = Number(hours);
  const m = Number(minutes);
  if (!h && !m) return '';
  if (!h) return `${m}min`; // "0h45" se lê mal
  if (!m) return `${h}h`;
  return `${h}h${String(m).padStart(2, '0')}`;
}

// Coluna "Expedição" da listagem — cobre os dois modos.
function formatExpedition(c) {
  if (isExpressCarrier(c)) return formatExpeditionHours(c.time_expedition_hours) || '—';
  // 0 é valor válido ("mesmo dia até o corte"), então não cai no travessão.
  const dias = c.time_expedition;
  if (dias === null || dias === undefined || dias === '') return '—';
  if (Number(dias) === 0) return 'Mesmo dia';
  return `${dias} ${Number(dias) > 1 ? 'dias' : 'dia'}`;
}

// Resumo compacto dos dias de trabalho pra listagem
function worksSummary(c) {
  const on = WEEK_DAYS.filter((d) => c[d.key]);
  if (on.length === 0) return '—';
  if (on.length === 7) return 'Todos os dias';
  const monFri = ['works_monday', 'works_tuesday', 'works_wednesday', 'works_thursday', 'works_friday'];
  const isMonFri = monFri.every((k) => c[k]) && !c.works_saturday && !c.works_sunday;
  if (isMonFri) return 'Seg–Sex';
  const isMonSat = monFri.every((k) => c[k]) && c.works_saturday && !c.works_sunday;
  if (isMonSat) return 'Seg–Sáb';
  return on.map((d) => d.label).join(', ');
}

// Normaliza uma transportadora vinda da API pro formato do formulário
function normalizeForForm(c) {
  const express = isExpressCarrier(c);
  return {
    supplier_name: c.supplier_name || '',
    name: c.name || '',
    // O modo sai do dado: quem tem hora cadastrada abre em "Em horas".
    expedition_mode: express ? EXPEDITION_MODES.hours : EXPEDITION_MODES.days,
    // Os dois campos são carregados mesmo no modo oposto. Quem alternar o
    // seletor por engano não perde o valor que já estava cadastrado.
    time_expedition: c.time_expedition ?? 0,
    time_expedition_hours: toTimeInput(c.time_expedition_hours),
    time_cut: toTimeInput(c.time_cut),
    time_collection: toTimeInput(c.time_collection),
    cut_tolerance_minutes: c.cut_tolerance_minutes ?? 15,
    is_active: !!c.is_active,
    is_marketplace: !!c.is_marketplace,
    order_size: c.order_size || "",
    works_monday: c.works_monday ?? true,
    works_tuesday: c.works_tuesday ?? true,
    works_wednesday: c.works_wednesday ?? true,
    works_thursday: c.works_thursday ?? true,
    works_friday: c.works_friday ?? true,
    works_saturday: c.works_saturday ?? false,
    works_sunday: c.works_sunday ?? false,
  };
}

function CarriersExpedition(props) {
  const { location: { idPage } } = props;
  const [auth, setAuth] = useState(null);

  const [carriers, setCarriers] = useState([]);
  const [modal, setModal] = useState({ active: false, mode: 'create', id: 0 });
  const [form, setForm] = useState({ ...EMPTY_CARRIER });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);

  async function verifyAuth() {
    const { profile } = store.getState().user;
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage });
    if (res.data.message === 'User not authorized') return setAuth('denied');
    if (res.data.message === 'User authorized') return setAuth('authorized');
    return history.push({ pathname: '/', state: { from: props.location } });
  }

  async function getCarriers() {
    try {
      const res = await apiLabs.get('carrier');
      setCarriers(res.data?.items || []);
    } catch (error) {
      console.error('GET carrier failed:', error);
    }
  }

  useEffect(() => {
    verifyAuth();
    getCarriers();
  }, []); // eslint-disable-line

  // ── MODAL (criar/editar) ────────────────────────────────────────────────
  function openCreate() {
    setForm({ ...EMPTY_CARRIER });
    setModal({ active: true, mode: 'create', id: 0 });
  }
  function openEdit(id) {
    const found = carriers.find((c) => c.id === id);
    if (!found) return;
    setForm(normalizeForForm(found));
    setModal({ active: true, mode: 'edit', id });
  }
  function closeModal() {
    setModal({ active: false, mode: 'create', id: 0 });
    setForm({ ...EMPTY_CARRIER });
  }

  function handleField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function applyPreset(preset) {
    const map = {
      weekdays: { mon: 1, tue: 1, wed: 1, thu: 1, fri: 1, sat: 0, sun: 0 },
      mon_sat: { mon: 1, tue: 1, wed: 1, thu: 1, fri: 1, sat: 1, sun: 0 },
      all: { mon: 1, tue: 1, wed: 1, thu: 1, fri: 1, sat: 1, sun: 1 },
    }[preset];
    setForm((f) => ({
      ...f,
      works_monday: !!map.mon, works_tuesday: !!map.tue, works_wednesday: !!map.wed,
      works_thursday: !!map.thu, works_friday: !!map.fri,
      works_saturday: !!map.sat, works_sunday: !!map.sun,
    }));
  }

  async function submit(e) {
    e.preventDefault();
    const isExpress = form.expedition_mode === EXPEDITION_MODES.hours;
    const payload = {
      supplier_name: (form.supplier_name || '').trim(),
      name: (form.name || '').trim(),
      // Exatamente UM dos dois vai preenchido — o backend recusa os dois com
      // 400. No modo horas, time_expedition tem que ser null de verdade:
      // Number('') || 0 daria 0, que o backend lê como valor presente.
      // ZERO é valor válido (não soma dias até o corte; depois do corte soma
      // 1 dia útil). Por isso campo vazio vira null — e não 0 via
      // `Number('') || 0`, que confundiria "não informei" com "zero dias".
      time_expedition: isExpress ? null : parseDiasExpedicao(form.time_expedition),
      time_expedition_hours: isExpress ? form.time_expedition_hours || null : null,
      // Corte, tolerância e dias de trabalho não entram no cálculo expresso
      // (a janela é controlada na VTEX), mas continuam sendo enviados: ainda
      // não há confirmação do backend de que viraram opcionais. Quando houver,
      // é aqui que passam a ir null no modo horas.
      time_cut: form.time_cut || null,
      time_collection: form.time_collection || null,
      cut_tolerance_minutes:
        form.cut_tolerance_minutes === '' || form.cut_tolerance_minutes == null
          ? 15
          : Number(form.cut_tolerance_minutes),
      is_active: !!form.is_active,
      is_marketplace: !!form.is_marketplace,
      // Sem classificacao -> null (o backend aceita omitido/null)
      order_size: form.order_size || null,
      works_monday: !!form.works_monday,
      works_tuesday: !!form.works_tuesday,
      works_wednesday: !!form.works_wednesday,
      works_thursday: !!form.works_thursday,
      works_friday: !!form.works_friday,
      works_saturday: !!form.works_saturday,
      works_sunday: !!form.works_sunday,
    };

    if (!payload.supplier_name) {
      toast.warn('Informe o nome da transportadora (IDW).');
      return;
    }

    // Sem isso o backend devolveria 400 com mensagem genérica — melhor dizer
    // aqui qual campo falta.
    if (isExpress && !payload.time_expedition_hours) {
      toast.warn('Informe o tempo de expedição em horas.');
      return;
    }
    if (!isExpress && payload.time_expedition === null) {
      toast.warn('Informe os dias de expedição (0 é válido).');
      return;
    }

    setSaving(true);
    try {
      if (modal.mode === 'edit') {
        await apiLabs.put(`carrier/${modal.id}`, payload);
        toast.success('Transportadora atualizada com sucesso', { autoClose: 5000 });
      } else {
        await apiLabs.post('carrier', payload);
        toast.success('Transportadora cadastrada com sucesso', { autoClose: 5000 });
      }
      closeModal();
      getCarriers();
    } catch (error) {
      toast.error('Falha ao salvar transportadora. Entre em contato com a equipe SantoLabs^.', { autoClose: 5000 });
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id, label) {
    if (!window.confirm(`Remover a transportadora "${label || ''}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await apiLabs.delete(`carrier/${id}`);
      toast.success('Transportadora removida com sucesso', { autoClose: 5000 });
      getCarriers();
    } catch (error) {
      toast.error('Falha ao remover transportadora. Entre em contato com a equipe SantoLabs^.', { autoClose: 5000 });
    }
  }

  const styles = stylePagesEmbarque();

  // Modo horas no formulário aberto — apaga os campos que não entram no
  // cálculo expresso e troca o campo de prazo.
  const isExpressForm = form.expedition_mode === EXPEDITION_MODES.hours;

  // Filtragem por busca (nome IDW ou abreviação, case-insensitive)
  const q = search.trim().toLowerCase();
  const filteredCarriers = !q ? carriers : carriers.filter((c) => {
    return (c.supplier_name || '').toLowerCase().includes(q)
        || (c.name || '').toLowerCase().includes(q);
  });

  return (
    <Dashboard title="Transportadoras">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <Container maxWidth="lg" className={styles.pageWrap}>
          <div className={styles.pageHeader}>
            <div className={styles.headerText}>
              <h1>Transportadoras</h1>
              <p>Gerenciamento de horários de corte, coleta e dias de operação</p>
            </div>
            <div className={styles.headerActions}>
              <Button
                className={styles.holidayBtn}
                variant="outlined"
                onClick={() => setHolidayModalOpen(true)}
                disableElevation
              >
                📅 Feriados & Folgas
              </Button>
              <Button className={styles.newBtn} variant="contained" onClick={openCreate} disableElevation>
                + Nova Transportadora
              </Button>
            </div>
          </div>

          <div className={styles.toolbar}>
            <TextField
              className={styles.searchInput}
              variant="outlined"
              size="small"
              placeholder="Buscar por nome ou abreviação…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <Search style={{ color: '#94a3b8', marginRight: 8 }} />,
              }}
            />
            <div className={styles.counter}>
              <strong>{filteredCarriers.length}</strong>
              {filteredCarriers.length !== carriers.length && ` de ${carriers.length}`}
              &nbsp;transportadora{filteredCarriers.length !== 1 ? 's' : ''}
            </div>
          </div>

          <div className={styles.tableCard}>
            <table className={styles.tableCarriers} cellSpacing={0}>
              <thead>
                <tr>
                  <th>Transportadora (IDW)</th>
                  <th>Abreviação</th>
                  <th>Corte</th>
                  <th>Última coleta</th>
                  <th>Expedição</th>
                  <th>Porte</th>
                  <th>Tolerância</th>
                  <th>Dias</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredCarriers.length === 0 && (
                  <tr>
                    <td colSpan={9} className={styles.emptyState}>
                      {carriers.length === 0
                        ? <>Nenhuma transportadora cadastrada. Clique em <strong>"+ Nova Transportadora"</strong> para começar.</>
                        : <>Nenhuma transportadora encontrada para <strong>"{search}"</strong>.</>}
                    </td>
                  </tr>
                )}
                {filteredCarriers.map((c) => (
                  <tr key={c.id}>
                    <td>{c.supplier_name || '—'}</td>
                    <td>{c.name || '—'}</td>
                    {/* Corte, tolerância e dias não entram no cálculo expresso.
                        Exibir o que está gravado faria a listagem mentir — uma
                        expressa apareceria com corte às 16:00 e Seg–Sex. */}
                    <td>
                      {isExpressCarrier(c)
                        ? '—'
                        : <span className={styles.timeBadge}>{toTimeInput(c.time_cut) || '—'}</span>}
                    </td>
                    <td><span className={styles.timeBadge}>{toTimeInput(c.time_collection) || '—'}</span></td>
                    <td>
                      {formatExpedition(c)}
                      {isExpressCarrier(c) && <span className={styles.expressBadge}>expressa</span>}
                    </td>
                    <td>
                      {c.order_size ? (
                        <span className={`${styles.sizeBadge} ${c.order_size}`}>
                          {orderSizeLabel(c.order_size)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{isExpressCarrier(c) ? '—' : `${c.cut_tolerance_minutes ?? 15} min`}</td>
                    <td>
                      {isExpressCarrier(c)
                        ? '—'
                        : <span className={styles.daysBadge}>{worksSummary(c)}</span>}
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${c.is_active ? 'active' : 'inactive'}`}>
                        <span className="dot" />
                        {c.is_active ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                    <td>
                      <IconButton className={styles.actionBtn} size="small" onClick={() => openEdit(c.id)} title="Editar">
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton className={`${styles.actionBtn} danger`} size="small" onClick={() => onDelete(c.id, c.supplier_name)} title="Remover">
                        <DeleteForeverOutlined fontSize="small" />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── MODAL CRIAR / EDITAR ─────────────────────────────────────── */}
          <Modal open={modal.active} onClose={closeModal}>
            <Box className={styles.modalBox}>
              <div className={styles.modalHeader}>
                <div>
                  <h2>{modal.mode === 'edit' ? 'Editar Transportadora' : 'Nova Transportadora'}</h2>
                  <span className="subtitle">
                    {modal.mode === 'edit'
                      ? 'Atualize os dados de operação e horários'
                      : 'Cadastre uma nova transportadora no sistema'}
                  </span>
                </div>
                <IconButton className="closeBtn" size="small" onClick={closeModal}>
                  <Close />
                </IconButton>
              </div>

              <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                <div className={styles.modalBody}>

                  {/* ── SEÇÃO: IDENTIFICAÇÃO ─────────────────────────────── */}
                  <div className={styles.formSection}>
                    <div className={styles.sectionTitle}>Identificação</div>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={7}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Nome Transportadora (IDW)</InputLabel>
                          <OutlinedInput
                            value={form.supplier_name}
                            onChange={(e) => handleField('supplier_name', e.target.value)}
                            label="Nome Transportadora (IDW)"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Abreviação</InputLabel>
                          <OutlinedInput
                            value={form.name}
                            onChange={(e) => handleField('name', e.target.value)}
                            label="Abreviação"
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </div>

                  {/* ── SEÇÃO: TEMPOS ────────────────────────────────────── */}
                  <div className={styles.formSection}>
                    <div className={styles.sectionTitle}>Tempos de operação</div>

                    {/* Modalidade: decide qual campo de prazo aparece abaixo */}
                    <RadioGroup
                      row
                      value={form.expedition_mode}
                      onChange={(e) => handleField('expedition_mode', e.target.value)}
                      className={styles.modeRow}
                    >
                      <FormControlLabel
                        value={EXPEDITION_MODES.days}
                        control={<Radio color="primary" size="small" />}
                        label="Em dias"
                      />
                      <FormControlLabel
                        value={EXPEDITION_MODES.hours}
                        control={<Radio color="primary" size="small" />}
                        label="Em horas (entrega expressa)"
                      />
                    </RadioGroup>

                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        {isExpressForm ? (
                          <FormControl fullWidth variant="outlined" size="small">
                            <InputLabel shrink>Tempo de expedição</InputLabel>
                            <OutlinedInput
                              type="time"
                              notched
                              label="Tempo de expedição"
                              value={form.time_expedition_hours}
                              onChange={(e) => handleField('time_expedition_hours', e.target.value)}
                            />
                          </FormControl>
                        ) : (
                          <FormControl fullWidth variant="outlined" size="small">
                            <InputLabel shrink>Dias de expedição</InputLabel>
                            <OutlinedInput
                              type="number"
                              inputProps={{ min: 0 }}
                              notched
                              label="Dias de expedição"
                              value={form.time_expedition}
                              onChange={(e) => handleField('time_expedition', e.target.value)}
                            />
                          </FormControl>
                        )}
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControl fullWidth variant="outlined" size="small" disabled={isExpressForm}>
                          <InputLabel shrink>Hora de corte</InputLabel>
                          <OutlinedInput
                            type="time"
                            notched
                            label="Hora de corte"
                            value={form.time_cut}
                            onChange={(e) => handleField('time_cut', e.target.value)}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel shrink>Última coleta</InputLabel>
                          <OutlinedInput
                            type="time"
                            notched
                            label="Última coleta"
                            value={form.time_collection}
                            onChange={(e) => handleField('time_collection', e.target.value)}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControl fullWidth variant="outlined" size="small" disabled={isExpressForm}>
                          <InputLabel shrink>Tolerância (min)</InputLabel>
                          <OutlinedInput
                            type="number"
                            inputProps={{ min: 0 }}
                            notched
                            label="Tolerância (min)"
                            value={form.cut_tolerance_minutes}
                            onChange={(e) => handleField('cut_tolerance_minutes', e.target.value)}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>

                    {isExpressForm && (
                      <p className={styles.modeHint}>
                        Na entrega expressa o prazo conta da criação do pedido,
                        sem corte e sem feriado. Corte, tolerância e dias de
                        trabalho não entram nesse cálculo — a janela de
                        funcionamento é controlada na VTEX.
                      </p>
                    )}
                  </div>

                  {/* ── SEÇÃO: DIAS DE TRABALHO ──────────────────────────── */}
                  <div className={`${styles.formSection} ${isExpressForm ? styles.sectionMuted : ''}`}>
                    <div className={styles.sectionTitle}>
                      Dias de trabalho
                      {isExpressForm && <span className={styles.notApplies}>não se aplica</span>}
                    </div>
                    <div className={styles.presetsRow}>
                      <Button size="small" variant="outlined" disabled={isExpressForm} className={styles.presetBtn} onClick={() => applyPreset('weekdays')}>Seg a Sex</Button>
                      <Button size="small" variant="outlined" disabled={isExpressForm} className={styles.presetBtn} onClick={() => applyPreset('mon_sat')}>Seg a Sáb</Button>
                      <Button size="small" variant="outlined" disabled={isExpressForm} className={styles.presetBtn} onClick={() => applyPreset('all')}>Todos os dias</Button>
                    </div>
                    <div className={styles.daysWrap}>
                      {WEEK_DAYS.map((d) => (
                        <FormControlLabel
                          key={d.key}
                          control={
                            <Checkbox
                              color="primary"
                              disabled={isExpressForm}
                              checked={!!form[d.key]}
                              onChange={(e) => handleField(d.key, e.target.checked)}
                              name={d.key}
                            />
                          }
                          label={d.label}
                        />
                      ))}
                    </div>
                  </div>

                  {/* ── SEÇÃO: OPÇÕES ────────────────────────────────────── */}
                  <div className={styles.formSection}>
                    <div className={styles.sectionTitle}>Opções</div>
                    <Grid container spacing={2} style={{ marginBottom: 4 }}>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel shrink htmlFor="order-size-select">
                            Porte do pedido
                          </InputLabel>
                          <OutlinedInput
                            id="order-size-select"
                            notched
                            label="Porte do pedido"
                            inputComponent="select"
                            inputProps={{
                              value: form.order_size || '',
                              onChange: (e) => handleField('order_size', e.target.value),
                              children: [
                                <option key="" value="">Sem classificação</option>,
                                ...ORDER_SIZE_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                )),
                              ],
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <FormControlLabel
                        control={<Checkbox color="primary" checked={!!form.is_marketplace} onChange={(e) => handleField('is_marketplace', e.target.checked)} name="is_marketplace" />}
                        label="Marketplace"
                      />
                      <FormControlLabel
                        control={<Switch color="primary" checked={!!form.is_active} onChange={(e) => handleField('is_active', e.target.checked)} name="is_active" />}
                        label="Ativa"
                      />
                    </div>
                  </div>

                </div>

                <div className={styles.modalFooter}>
                  <Button className={styles.cancelBtn} onClick={closeModal} disabled={saving}>
                    Cancelar
                  </Button>
                  <Button type="submit" className={styles.submitBtn} disabled={saving} disableElevation variant="contained">
                    {saving ? 'Salvando…' : (modal.mode === 'edit' ? 'Salvar alterações' : 'Cadastrar')}
                  </Button>
                </div>
              </form>
            </Box>
          </Modal>

          {/* ── MODAL DE FERIADOS ──────────────────────────────────────── */}
          <HolidayModal
            open={holidayModalOpen}
            onClose={() => setHolidayModalOpen(false)}
          />
        </Container>
      )}
    </Dashboard>
  );
}

export default CarriersExpedition;
